/**
 * Page - Base class for full-page components
 *
 * Foundation for all page components. Integrates with Base.js routing system.
 * When URL changes, Base loads appropriate Page based on routing configuration.
 *
 * ARCHITECTURE: Extends View (Ancestor → Class → Controller → Module → View → Page)
 *
 * INTEGRATION:
 * - Loaded by Base.js via routing (handlePath → response.page)
 * - Managed in Base.instances.page (only one page active at a time)
 * - Automatic analytics tracking
 * - Automatic scroll handling
 * - Viewport visibility transitions
 *
 * KEY FEATURES:
 * - Full viewport height (vp-min-height class)
 * - Fade-in transition by default
 * - Auto page-view tracking
 * - Responsive slide transitions
 * - Integration with global scroll handlers
 *
 * @class Page
 * @extends View
 *
 * @example
 * // Create custom page
 * const MyPage = Page.inherit({
 *     namespace: 'Pages/MyPage',
 *     defaults: {
 *         template: 'Pages/MyPage'
 *     },
 *     constructor(setup) {
 *         // Page initialization
 *     }
 * });
 */
define("Page", [`View`, `module`], function (Super, Self) {
	return Super.inherit({
		namespace: Self.id,
		defaults: {
			hide: true, // hide container as the constructors begin
			show: true, // show container after constructors complete
			transition: `fade`, // fade in
		},
		prepare(setup) {
			const { data } = this;

			// used to allow inheriting classes to inject parameters for Insights tracking
			data.trackingOptions = {
				custom: {},
			};
		},
		constructor(setup) {
			this.addClass(`Page`); // make sure the container has the right class for styling purposes
			this.addClass(`vp-min-height`);
		},
		ready(setup) {
			const { Insights, App, DOM, data, elements } = this;
			const { trackingOptions, uid, event: eventObj, cart } = data;
			const group = this.isObject(eventObj) ? eventObj.group : null;
			const group_id = this.isObject(group) ? group.id : null;
			const slideElements = this.select(`> .body > .hero-wrapper > .content`);
			const slides = this.map(slideElements, (element) => {
				const ignore = this.hasClass(`no-transition`, element);
				return { element, ignore };
			});
			const event = `resize`;
			const target = window;
			const trigger = `visible`;
			const handler = this.refresh;

			elements.slides = slides;

			App.onScroll(handler);
			DOM.on({ event, handler, target, trigger });

			Insights.setUid({ uid }); // NOTE: can be undefined

			this.assign(trackingOptions, { cart, group_id });

			trackingOptions.eventType = `page-view`;
			if (trackingOptions.pageType === undefined) {
				trackingOptions.pageType = `Other`;
			}

			App.captureInsight(trackingOptions);
		},

		api: {
			/**
			 * @method Page#refresh
			 */
			refresh: {
				value() {
					const { DOM, elements } = this;
					this.each(elements.slides, (slide) => {
						const { element, ignore } = slide;

						if (!ignore) {
							if (DOM.inViewport(element)) {
								this.removeClass(`invisible`, element);
							} else {
								this.addClass(`invisible`, element);
							}
						}
					});
				},
			},

			/**
			 * @method Page#getTitle
			 * @param {Object} context
			 * @returns {string|undefined}
			 */
			getTitle: {
				public: true,
				value(context) {
					return this.data.title;
				},
			},
		},

		listeners: [
			{
				trigger: `visible`,
				handler() {
					this.defer({
						done: true,
						then: this.refresh,
					});

					this.defer({
						done: true,
						duration: 500,
						then: this.refresh,
					});
				},
			},
		],
	});
});

define("Page.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Page/default"] = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    return "<div class=\"head vp-gutters\"></div> <div class=\"body\"></div> <div class=\"foot vp-gutters\"></div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Page.js.map